import {Component, computed, ElementRef, inject, Injector, input, OnInit, viewChild, signal, ChangeDetectionStrategy} from "@angular/core"
import {CM_LOGGER_MATERIALS} from "@common/services/logging"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatDialogModule} from "@angular/material/dialog"
import {MatInputModule} from "@angular/material/input"
import {MatMenuModule} from "@angular/material/menu"
import {MatSelectModule} from "@angular/material/select"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterModule} from "@angular/router"
import {
    CopyMaterialGQL,
    CreateMaterialGQL,
    GetMaterialsGQL,
    GetMaterialsQuery,
    MaterialsGridCreateDataObjectAssignmentGQL,
    MaterialsGridItemFragment,
} from "@app/platform/components/materials/materials-grid/materials-grid.generated"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {
    ContentTypeModel,
    DataObjectAssignmentType,
    MaterialOrderByCriteria,
    MaterialState,
    MutationCreateMaterialInput,
    MutationUpdateMaterialInput,
    NextActor,
    PaymentState,
    SortOrder,
} from "@generated"
import {ItemListComponent} from "@common/components/item/item-list/item-list.component"
import {ListInfoComponent} from "@common/components/item/list-info/list-info.component"
import {InfiniteListComponent} from "@common/components/lists/infinite-list/infinite-list.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {AdvancedAction, BatchDownloadData, ExtraBatchActionItem} from "@common/models/item/list-item"
import {Labels, StateLabel} from "@labels"
import {MaterialExplorerInfoGQL} from "@platform/components/materials/material-explorer/material-explorer.generated"
import {MaterialsCardComponent} from "@platform/components/materials/materials-card/materials-card.component"
import {BatchUpdateMaterialsGQL, GetMaterialsQueryVariables, UpdateMaterialGQL} from "@platform/components/materials/materials-grid/materials-grid.generated"
import {CancellableRequest} from "@platform/models/data/cancellable-request"
import {MaterialBatchOperationService} from "@platform/services/material/material-batch-operation.service"
import {UploadGqlService} from "@app/common/services/upload/upload.gql.service"
import {AddMaterialDialogComponent} from "@platform/components/materials/add-material-dialog/add-material-dialog.component"
import {MaterialOutputService} from "@app/platform/services/material/material-output.service"
import {MeshGeometryService} from "@app/template-editor/services/mesh-geometry.service"
import {BakeTextureService} from "@app/template-editor/services/bake-texture.service"

export type CreateMaterialData = MutationCreateMaterialInput & {materialRangeTagId?: string | null}
export type CreatedMaterialType =
    | {
          createMethod: "BATCH"
          item: {organizationId: string; materialRangeTagId?: string}
      }
    | {
          createMethod: "SINGLE"
          item: MaterialsGridItemFragment
      }
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        InfiniteListComponent,
        ListInfoComponent,
        MatButtonModule,
        MatDialogModule,
        MatInputModule,
        MatMenuModule,
        MatSelectModule,
        RouterModule,
        MatTooltipModule,
        MaterialsCardComponent,
        AddMaterialDialogComponent,
    ],
    providers: [SceneManagerService, MaterialBatchOperationService, MaterialOutputService, MeshGeometryService, BakeTextureService],
    selector: "cm-materials-grid",
    styleUrls: ["materials-grid.component.scss"],
    templateUrl: "materials-grid.component.html",
})
export class MaterialsGridComponent extends ItemListComponent<MaterialsGridItemFragment, MutationUpdateMaterialInput, CreatedMaterialType> implements OnInit {
    public readonly $viewMode = input<"materialLibrary" | undefined>(undefined, {alias: "viewMode"})

    readonly $stateLabels = signal<StateLabel<MaterialState>[]>([])
    readonly $extraBatchActions = signal<ExtraBatchActionItem<MaterialsGridItemFragment>[]>([])
    readonly $batchDownloadData = signal<BatchDownloadData<MaterialsGridItemFragment>[]>([])
    readonly $advancedActions = signal<AdvancedAction[]>([])
    readonly $newItemBatchMode = signal(false)
    readonly $showMaterialExplorer = signal(false)
    public enableBatchUpdates = false

    readonly $fileInput = viewChild.required<ElementRef>("fileInput")

    protected readonly uploadService = inject(UploadGqlService)
    protected readonly materialBatchOperationService = inject(MaterialBatchOperationService)
    protected readonly sceneManagerService = inject(SceneManagerService)
    protected override readonly logger = inject(CM_LOGGER_MATERIALS)

    readonly materialExplorerInfoGql = inject(MaterialExplorerInfoGQL)
    readonly batchUpdateMaterialsGql = inject(BatchUpdateMaterialsGQL)
    readonly createDataObjectAssignmentGql = inject(MaterialsGridCreateDataObjectAssignmentGQL)
    readonly copyMaterialGql = inject(CopyMaterialGQL)

    readonly materialsGql = inject(GetMaterialsGQL)
    readonly injector = inject(Injector)

    private fetchRequest = new CancellableRequest<GetMaterialsQuery, GetMaterialsQueryVariables>(this.materialsGql, this.injector, this.destroyRef)

    private readonly $filterFunction = computed(() => {
        const viewMode = this.$viewMode()
        if (viewMode === "materialLibrary") return this.filters.materialLibraryFilter
        else return this.filters.materialFilter
    })

    override ngOnInit() {
        this.$stateLabels.set(Array.from(Labels.MaterialState.values()))
        this.materialBatchOperationService.getExtraBatchActions().then((actions) => this.$extraBatchActions.set(actions))

        this.$batchDownloadData.set(this.materialBatchOperationService.getBatchDownloadData())

        const organizationDetails = this.organizations.current
        if (organizationDetails) {
            fetchThrowingErrors(this.materialExplorerInfoGql)({organizationId: organizationDetails.id}).then((materialExplorerInfo) => {
                this.$showMaterialExplorer.set(
                    this.$can().read.material(null, "explorer") &&
                        materialExplorerInfo.organization?.matExplorerMaterial?.id !== undefined &&
                        materialExplorerInfo.organization?.matExplorerTemplate?.id !== undefined,
                )
            })
        }

        this.$advancedActions.set(this.materialBatchOperationService.getAdvancedActions(this.$fileInput()))

        this.sceneManagerService.$lodType.set("pathTraced")
        this.sceneManagerService.$computeDummyReifiedMeshData.set(true)

        super.ngOnInit()
    }

    // OVERLOADS

    protected override _contentTypeModel = ContentTypeModel.Material

    protected override _batchUpdate = async (
        property: "addTagId" | "assignUserId" | "nextActor" | "paymentState" | "removeTagId" | "removeUserAssignment" | "state",
        value: string | boolean,
    ): Promise<number> => {
        const material = await mutateThrowingErrors(this.batchUpdateMaterialsGql)({
            filter: this.$filterFunction()(),
            [property]: value,
        })
        return material.batchUpdateMaterials
    }

    protected override _fetchList = ({skip, take}: {skip: number; take: number}) => {
        return this.fetchRequest
            .fetch({
                take,
                skip,
                filter: this.$filterFunction()(),
                orderBy: [
                    {key: MaterialOrderByCriteria.Priority, direction: SortOrder.Asc},
                    {key: MaterialOrderByCriteria.Id, direction: SortOrder.Desc},
                ],
            })
            .then(({materials, materialsCount}) => ({items: materials, totalCount: materialsCount}))
    }

    protected override _refreshItem = ({id, legacyId}: {id?: string; legacyId?: number}): Promise<MaterialsGridItemFragment | undefined> =>
        fetchThrowingErrors(this.materialsGql)({
            take: 1,
            filter: {
                ...this.$filterFunction()(),
                id: id ? {equals: id} : undefined,
                legacyId: legacyId ? {equals: legacyId} : undefined,
            },
        }).then(({materials}) => materials?.[0] || undefined)

    readonly updateMaterialGql = inject(UpdateMaterialGQL)
    protected override _updateItem = (data: MutationUpdateMaterialInput) =>
        mutateThrowingErrors(this.updateMaterialGql)({
            input: data,
        }).then(({updateMaterial: material}) => material)

    readonly createMaterialGql = inject(CreateMaterialGQL)
    protected override _createItem = undefined

    private createMaterial = (data: Partial<CreateMaterialData>) => {
        const {materialRangeTagId, tagAssignments, name, ...rest} = data
        if (name === undefined) throw new Error("Name is required")
        return mutateThrowingErrors(this.createMaterialGql)({
            input: {
                ...rest,
                name,
                tagAssignments: materialRangeTagId ? [materialRangeTagId, ...(tagAssignments ?? [])] : tagAssignments,
                state: rest.state ?? MaterialState.Draft,
                paymentState: rest.paymentState ?? PaymentState.OrderPlaced,
                meshSpecific: rest.meshSpecific ?? false,
                nextActor: rest.nextActor ?? NextActor.Team1,
            },
        }).then(({createMaterial: material}) => material)
    }

    batchCreateMaterials(event: Event) {
        const files = (<HTMLInputElement>event.target).files
        if (!files || files.length === 0) return

        const newItemDialog = this.$newItemDialog()
        if (!newItemDialog) {
            // put an `<ng-template #newItemDialog>` tag containing the form in the html template
            throw new Error("Missing newItemDialog ref in template")
        }

        this.$newItemBatchMode.set(true)
        this.newItemDialogRef = this.dialog.open(newItemDialog, {
            data: this._initialNewItemData(),
        })
        this.newItemDialogRef.afterClosed().subscribe(async (data) => {
            this.$newItemBatchMode.set(false)

            if (!data) {
                return
            }

            if (data.createMethod !== "BATCH") {
                return
            }

            const {organizationId} = data.item

            if (!organizationId) return

            try {
                for (const file of Array.from(files)) {
                    try {
                        const dataObject = await this.uploadService.createAndUploadDataObject(
                            file,
                            {
                                organizationId: organizationId,
                            },
                            {processUpload: true, showUploadToolbar: true},
                        )

                        const material = await this.createMaterial({
                            ...data.item,
                            name: file.name.replace(/\.[^/.]+$/, ""),
                            state: MaterialState.InfoReview,
                        })

                        if (!material) {
                            throw Error("Returned material is undefined")
                        }

                        await mutateThrowingErrors(this.createDataObjectAssignmentGql)({
                            input: {
                                dataObjectId: dataObject.id,
                                objectId: material.id,
                                contentTypeModel: ContentTypeModel.Material,
                                type: DataObjectAssignmentType.GalleryImage,
                            },
                        })
                    } catch (error) {
                        this.snackBar.open("Cannot create new material.", "", {duration: 3000})
                        this.logger.error("Failed to create material", error)
                    }
                }
            } finally {
                this.refresh.contentTypeModel(ContentTypeModel.Material)
            }
        })
    }

    async copyMaterial(item: {id: string; legacyId: number}): Promise<void> {
        try {
            const {copyMaterial: copiedMaterial} = await mutateThrowingErrors(this.copyMaterialGql)({
                materialId: item.id,
                organizationId: this.organizations.current?.id,
            })

            this.notifications.showInfo(`Material with the id ${item.legacyId} has been copied. The id of the copy is ${copiedMaterial.legacyId}.`)

            setTimeout(() => {
                void this.router.navigate([copiedMaterial.id], {
                    relativeTo: this.route,
                    queryParamsHandling: "preserve",
                })
            })
        } finally {
            this.refresh.contentTypeModel(ContentTypeModel.Material)
        }
    }
}
